import { useState, useEffect, useCallback } from "react";
import {
  Contribution,
  AccessibilitySection,
  MediaSection as MediaSectionType,
  ApiEntryPoint,
  ApiBuilding,
  DetailedNote,
} from "@/types/common";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Lock } from "@mui/icons-material";
import ENDPOINTS from "../../api/endpoints";
import BottomNavBar from "@/common/BottomNavBar";
import useApiClient from "@/api/Client";
import GenericCardSkeleton from "@/shared/components/GenericCardSkeleton";
import { useNavigate, useParams } from "react-router-dom";
import Icons from "@/assets";
import {
  ArrowBack,
  ExpandMore,
  AccessTime,
  Cancel,
  ApartmentOutlined as ApartmentOutlinedIcon,
} from "@mui/icons-material";
import { getEntryPointIcon } from "@/utils/buildingIcons";
import MediaSection from "@/components/contributionDetails/MediaSection";
import NotesSection from "@/components/contributionDetails/NotesSection";
import { TbPercentage40 } from "react-icons/tb";
import { FaCircleCheck } from "react-icons/fa6";

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    maxWidth: "100%",
    backgroundColor: "#f5f5f5",
    overflow: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "4%",
    backgroundColor: "#fff",
    gap: "3%",
    position: "sticky",
    top: 0,
    zIndex: 1100,
    boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
  },
  title: {
    fontSize: { xs: "3.88vw", sm: 16 },
    fontWeight: 700,
    color: "#000",
  },
  placeCard: {
    display: "flex",
    gap: "2.5%",
    px: "4%",
    pt: "4%",
  },
  placeImage: {
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    objectFit: "cover",
  },
  statusChip: {
    height: "24px",
    fontSize: { xs: "2.91vw", sm: 12 },
    fontWeight: 500,
    borderRadius: "4px",
  },
  card: {
    margin: "4%",
    borderRadius: "20px",
    backgroundColor: "#fff",
    overflow: "hidden",
    boxShadow: "0px 10px 20px 0px #0000000A",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    px: "4%",
    py: "1%",
    gap: "2vw",
  },
  icon: {
    width: { xs: "12.62vw", sm: 52 },
    height: { xs: "12.62vw", sm: 52 },
    borderRadius: "8px",
  },
  contentContainer: {
    margin: "0 4% 4% 4%",
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "4%",
    boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    p: "4%",
  },
  statusMessageContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.5vw",
    p: "2%",
    borderRadius: "8px",
    width: "90%",
    mx: "auto",
    mt: "3%",
  },
  statusMessageText: {
    fontSize: { xs: "2.91vw", sm: 12 },
    color: "#626262",
  },
  placeNameContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  placeNameText: {
    fontWeight: 700,
    fontSize: { xs: "3.88vw", sm: 16 },
  },
  placeAddressText: {
    fontSize: { xs: "2.91vw", sm: 12 },
    color: "#666",
    mt: "1%",
    mb: "2%",
  },
  editIcon: {
    width: { xs: "4.5vw", sm: 16 },
    height: { xs: "4.5vw", sm: 16 },
  },
  accordion: {
    "&:before": { display: "none" },
  },
  sectionIconImage: { width: "100%", height: "100%" },
  sectionTitleText: {
    flex: 1,
    fontWeight: 600,
    fontSize: { xs: "3.88vw", sm: 16 },
  },
  accordionDetails: { p: 0 },
  bottomSpacer: { pb: "20%" },
};

const LOCK_TTL_MS = 30 * 60 * 1000;

const isModerationLocked = (lockedAt?: string | null): boolean => {
  if (!lockedAt) return false;
  return Date.now() - new Date(lockedAt).getTime() < LOCK_TTL_MS;
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case "reviewed":
    case "replaced":
      return {
        label: "Reviewed",
        color: "#DBFFD7",
        textColor: "#2E7D32",
        icon: <FaCircleCheck fontSize={14} color="#74CE6A" />,
      };
    case "pending":
      return {
        label: "Under Review",
        color: "#FFF1C0",
        textColor: "#F2B12F",
        icon: <TbPercentage40 fontSize={14} color="#F2B12F" />,
      };
    case "needs_changes":
      return {
        label: "Needs Changes",
        color: "#FFF3E0",
        textColor: "#E65100",
        icon: <AccessTime sx={{ fontSize: 14 }} />,
      };
    case "rejected":
      return {
        label: "Rejected",
        color: "#FFEBEE",
        textColor: "#C62828",
        icon: <Cancel sx={{ fontSize: 14 }} />,
      };
    default:
      return {
        label: "Unknown",
        color: "#F5F5F5",
        textColor: "#757575",
        icon: null,
      };
  }
};

const ContributionDetails = () => {
  const { api } = useApiClient();
  const navigate = useNavigate();
  const { contribution_id } = useParams<{ contribution_id: string }>();
  const [contributionData, setContributionData] = useState<Contribution | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchContributionById = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(
        ENDPOINTS.CONTRIBUTION.GET_BY_ID(contribution_id!),
      );
      setContributionData(res?.data?.data || null);
    } catch (error) {
      console.error("Error fetching details:", error);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [api, contribution_id, navigate]);

  useEffect(() => {
    if (contribution_id) {
      fetchContributionById();
    }
  }, [contribution_id, fetchContributionById]);

  if (loading) {
    return (
      <Box sx={styles.pageContainer}>
        <Box sx={styles.header}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography sx={styles.title}>Contribution Details</Typography>
        </Box>
        <Box sx={styles.loadingContainer}>
          <GenericCardSkeleton hasStatusLine hasMediaRow mediaCount={2} />
        </Box>
      </Box>
    );
  }

  const {
    place,
    status,
    is_best,
    is_updated,
    buildings = [],
    moderation_locked_at,
  } = contributionData || ({} as Partial<Contribution>);

  const locked = isModerationLocked(moderation_locked_at) && !(status === "reviewed" && is_best) && status !== "replaced";

  const displayImage = (() => {
    for (const building of buildings || []) {
      for (const ep of building.entryPoints || []) {
        for (const m of ep.media || []) {
          const p = m.photos?.find((ph) => ph.url);
          if (p) return p.url;
        }
      }
    }
    return undefined;
  })();

  const statusConfig = getStatusConfig(status || "");

  return (
    <Box sx={styles.pageContainer}>
      {/* Header */}
      <Box sx={styles.header}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBack />
        </IconButton>
        <Typography sx={styles.title}>Contribution Details</Typography>
      </Box>

      {(status === "reviewed" || is_best) && (
        <Box
          sx={{
            ...styles.statusMessageContainer,
            border: `1px solid ${is_best ? "#9443DF" : "#939393"}`,
            backgroundColor: is_best ? "#F4E7FF" : "#FBFBFB",
          }}
        >
          <Box
            component="img"
            src={is_best ? Icons.Seleted : Icons.NotSelected}
          ></Box>
          <Typography sx={styles.statusMessageText}>
            {is_best
              ? "Added to place details"
              : "Not added to place details due to data mismatch"}
          </Typography>
        </Box>
      )}

      {status === "replaced" && !is_best && (
        <Box
          sx={{
            ...styles.statusMessageContainer,
            border: `1px solid #64B5F6`,
            backgroundColor: "#E3F2FD",
          }}
        >
          <Box component="img" src={Icons.Info} sx={{ width: 24, height: 24 }}></Box>
          <Typography sx={{ ...styles.statusMessageText }}>
            Replaced by a newer best contribution
          </Typography>
        </Box>
      )}

      {locked && (
        <Box
          sx={{
            ...styles.statusMessageContainer,
            border: "1px solid #F2B12F",
            backgroundColor: "#FFF8E7",
          }}
        >
          <Lock sx={{ fontSize: 16, color: "#F2B12F", flexShrink: 0 }} />
          <Typography sx={styles.statusMessageText}>
            This contribution is currently under moderation. Editing is
            temporarily unavailable.
          </Typography>
        </Box>
      )}

      {/* Place Info */}
      <Box sx={styles.placeCard} alignItems="start">
        <Box
          component="img"
          src={displayImage || Icons.placeImg}
          sx={styles.placeImage}
        />
        <Box>
          <Box sx={styles.placeNameContainer}>
            <Typography sx={styles.placeNameText}>{place?.name}</Typography>
          </Box>
          <Typography sx={styles.placeAddressText}>
            {place?.address_text}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "2%" }}>
          <Box alignItems="center" textAlign="end">
            <Chip
              icon={statusConfig.icon || undefined}
              label={statusConfig.label}
              size="small"
              sx={{
                ...styles.statusChip,
                backgroundColor: statusConfig.color,
                color: statusConfig.textColor,
                "& .MuiChip-icon": {
                  color: statusConfig.textColor,
                },
              }}
            />
            {is_updated && (
              <Typography
                sx={{
                  fontSize: { xs: "3.2vw", sm: 12 },
                  color: "#939393",
                  mt: 0.5,
                }}
              >
                {"(Edited)"}
              </Typography>
            )}
          </Box>
          {(status === "pending" || (status === "reviewed" && !is_best)) && (
            <IconButton
              size="small"
              disabled={locked}
              onClick={() => {
                localStorage.setItem("from_explore_to_contribute", "true");
                navigate(`/contribution/update/${contribution_id}`, {
                  state: { ...place },
                });
              }}
              sx={{ opacity: locked ? 0.35 : 1 }}
            >
              <Box component="img" src={Icons.Edit} sx={styles.editIcon} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={styles.bottomSpacer}>
        {(buildings || []).length === 0 && (
          <Box sx={{ p: "4%", textAlign: "center" }}>
            <Typography sx={{ color: "#999", fontSize: { xs: "3vw", sm: 13 } }}>
              No details available for this contribution yet.
            </Typography>
          </Box>
        )}

        {[...(buildings || [])]
          .sort((a: ApiBuilding, b: ApiBuilding) => a.order_index - b.order_index)
          .map((building: ApiBuilding) => (
            <Box key={building.building_id} sx={styles.card}>
              <Accordion elevation={0} sx={styles.accordion} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls={`building-${building.building_id}-content`}
                  id={`building-${building.building_id}-header`}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Box
                      sx={{
                        width: { xs: "10vw", sm: 42 },
                        height: { xs: "10vw", sm: 42 },
                        borderRadius: "12px",
                        backgroundColor: "#F0DEFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ApartmentOutlinedIcon sx={{ color: "#6f2fa8", fontSize: { xs: "5vw", sm: 22 } }} />
                    </Box>
                    <Typography sx={styles.sectionTitleText}>
                      {building.building_name}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: "12px" }}>
                  {/* ── Entry-point-level accordions ─────────── */}
                  {[...(building.entryPoints || [])]
                    .sort((a, b) => (a.buildingSection?.section_id ?? 0) - (b.buildingSection?.section_id ?? 0))
                    .map((ep: ApiEntryPoint) => {
                      // Adapt media for MediaSection component — do not filter out items marked incorrect by moderator
                      const sectionMedia: MediaSectionType[] = (ep.media || []).map((m) => ({
                        placeSection: { section_name: ep.entry_name },
                        photos: m.photos || [],
                        videos: m.video ? [m.video] : [],
                      }));

                      // Adapt placeNotes for NotesSection component — do not filter out notes marked incorrect by moderator
                      const sectionNotes: AccessibilitySection[] = (ep.placeNotes || []).map(
                        (pn) => ({
                          placeSection: { section_name: ep.entry_name },
                          default_notes: (pn.default_notes || []) as unknown as DetailedNote[],
                          user_notes: pn.user_notes ? pn.user_notes as { text: string; is_correct?: boolean } : null,
                        }),
                      );

                      const hasMedia = sectionMedia.some(
                        (m) => (m.photos?.length ?? 0) > 0 || (m.videos?.length ?? 0) > 0,
                      );
                      const hasNotes = sectionNotes.some(
                        (n) => (n.default_notes?.length ?? 0) > 0 || n.user_notes?.text,
                      );

                      if (!hasMedia && !hasNotes) return null;

                      const epIcon = getEntryPointIcon(ep);

                      return (
                        <Accordion
                          key={ep.entry_point_id}
                          elevation={0}
                          sx={{
                            ...styles.accordion,
                            border: "1px solid #F0E6FF",
                            borderRadius: "12px !important",
                            mb: "8px",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore sx={{ color: "#8D28E6" }} />}
                            aria-controls={`ep-${ep.entry_point_id}-content`}
                            id={`ep-${ep.entry_point_id}-header`}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <Box
                                sx={{
                                  width: { xs: "8vw", sm: 34 },
                                  height: { xs: "8vw", sm: 34 },
                                  borderRadius: "8px",
                                  backgroundColor: "#F0DEFF",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {epIcon}
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: { xs: "3.5vw", sm: 14 },
                                  color: "#0F172A",
                                }}
                              >
                                {ep.entry_name}
                              </Typography>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails sx={styles.accordionDetails}>
                            <Box sx={{ ...styles.contentContainer, mt: 0 }}>
                              {hasMedia && <MediaSection media={sectionMedia} />}
                              {hasNotes && <NotesSection data={sectionNotes} />}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
      </Box>

      <BottomNavBar />
    </Box>
  );
};

export default ContributionDetails;
